import { CommissionBreakdown } from '../../components/commissions/CommissionBreakdown';
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Tab,
  TabList,
  Tabs,
  Textarea,
  Typography,
} from "@mui/joy";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganizationUsers } from "../../hooks/useOrganizationUsers";
import { apiClient } from "../../lib/api";
import {
  bulkPayCommissions,
  fetchCommissions,
  fetchCommissionSummary,
  fetchPayouts,
} from "../../lib/apiCommissions";
import { ResponsiveTable } from "../../components/common/ResponsiveTable";
import { SavedBillingViews } from "../../components/billing/BillingWorkflowTools";
import type {
  CommissionFilters,
  SalesCommission,
} from "../../types/commissions";
import {
  csvCell,
  groupCommissions,
  reviewedSelection,
} from "./commissionReview";
const money = (value: unknown) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(value || 0),
  );
const date = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : "—";
const errorText = (error: any) => {
  const data = error?.responseData || error?.response?.data;
  return data
    ? typeof data === "string"
      ? data
      : Object.values(data).flat().join(" ")
    : error?.message || "Unable to complete this action. Please retry.";
};
function download(rows: unknown[][], name: string) {
  const url = URL.createObjectURL(
    new Blob([rows.map((row) => row.map(csvCell).join(",")).join("\r\n")], {
      type: "text/csv;charset=utf-8",
    }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}
export default function CommissionDashboard() {
  return <CommissionWorkspace />;
}
export function CommissionWorkspace({ mine = false }: { mine?: boolean }) {
  const { user } = useAuth();
  const { users } = useOrganizationUsers();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1),
    scope = params.toString() + String(mine) + user?.id;
  const filters: CommissionFilters = {
    page,
    search: params.get("search") || "",
    status: (params.get("status") || "") as CommissionFilters["status"],
    sales_rep: mine ? user?.id : Number(params.get("sales_rep")) || "",
    date_from: params.get("date_from") || "",
    date_to: params.get("date_to") || "",
    date_field: params.get("date_field") || "accrued_at",
  };
  const setFilter = (name: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    if (name !== "page") next.delete("page");
    setParams(next);
  };
  const key = [user?.organization, user?.id];
  const capabilities = useQuery({
    queryKey: ["commission-capabilities", ...key],
    queryFn: async () =>
      (
        await apiClient.get<{ can_manage: boolean }>(
          "/api/sales-commissions/capabilities/",
        )
      ).data,
  });
  const manage = !!capabilities.data?.can_manage;
  const list = useQuery({
    queryKey: ["commissions", ...key, filters],
    queryFn: () => fetchCommissions(filters),
  });
  const summary = useQuery({
    queryKey: ["commissionSummary", ...key, filters],
    queryFn: () => fetchCommissionSummary(filters),
  });
  const [tab, setTab] = useState("commissions");
  const [selection, setSelection] = useState<{ scope: string; ids: number[] }>({
    scope: "",
    ids: [],
  });
  const rows = list.data?.results || [],
    selected = reviewedSelection(rows, selection.ids, selection.scope, scope),
    groups = groupCommissions(selected),
    total = selected.reduce((sum, row) => sum + Number(row.final_amount), 0);
  const [payOpen, setPayOpen] = useState(false),
    [requestId, setRequestId] = useState(""),
    [method, setMethod] = useState("check"),
    [reference, setReference] = useState(""),
    [notes, setNotes] = useState("");
  const [detail, setDetail] = useState<number | null>(null),
    [action, setAction] = useState<{
      kind: "hold" | "override";
      row: SalesCommission;
    } | null>(null),
    [reason, setReason] = useState(""),
    [owner, setOwner] = useState<number | null>(null),
    [followUp, setFollowUp] = useState(""),
    [amount, setAmount] = useState("");
  const invalidate = () => {
    [
      "commissions",
      "commissionSummary",
      "myCommissions",
      "commissionPayouts",
      "commission-detail",
      "commission-activity",
    ].forEach((name) => qc.invalidateQueries({ queryKey: [name] }));
  };
  const payment = useMutation({
    mutationFn: () =>
      bulkPayCommissions({
        commission_ids: selected.map((row) => row.id),
        expected_total: total.toFixed(2),
        request_id: requestId,
        payment_method: method,
        payment_reference: reference,
        notes,
      }),
    onSuccess: () => {
      invalidate();
      setSelection({ scope: "", ids: [] });
      setPayOpen(false);
      setReference("");
      setNotes("");
    },
  });
  const change = useMutation({
    mutationFn: async ({
      id,
      kind,
      data,
    }: {
      id: number;
      kind: string;
      data: any;
    }) =>
      (await apiClient.post(`/api/sales-commissions/${id}/${kind}/`, data))
        .data,
    onSuccess: () => {
      invalidate();
      setAction(null);
      setReason("");
    },
  });
  const exportRows = useMutation({
    mutationFn: async () => {
      const all: SalesCommission[] = [];
      let next = true,
        p = 1;
      while (next) {
        const response = await fetchCommissions({ ...filters, page: p++ });
        if (response.count > 10000)
          throw Error(
            "Narrow the date range to export at most 10,000 commissions.",
          );
        all.push(...response.results);
        next = !!response.next;
      }
      download(
        [
          [
            "Job",
            "Sales rep",
            "Status",
            "Accrued",
            "Paid",
            "Structure",
            "Structure source",
            "Calculated",
            "Override",
            "Final amount",
            "Payout",
          ],
          ...all.map((row) => [
            row.job_number,
            row.sales_rep_name,
            row.status,
            row.accrued_at,
            row.paid_at,
            row.calculation_snapshot?.name || "Original formula",
            row.calculation_snapshot?.source || "legacy",
            row.commission_amount,
            row.override_amount,
            row.final_amount,
            row.payout_number,
          ]),
        ],
        "commission-statement.csv",
      );
    },
  });
  const openAction = (row: SalesCommission, kind: "hold" | "override") => {
    setAction({ kind, row });
    setReason("");
    setOwner(user?.id || null);
    setFollowUp("");
    setAmount(row.final_amount);
    change.reset();
  };
  const toggle = (id: number) =>
    setSelection({
      scope,
      ids: selected.some((row) => row.id === id)
        ? selected.filter((row) => row.id !== id).map((row) => row.id)
        : [...selected.map((row) => row.id), id],
    });
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box>
          <Typography level="h3">
            {mine ? "My Commissions" : "Commissions"}
          </Typography>
          <Typography level="body-sm">
            Review earnings, holds and recorded payouts.
          </Typography>
        </Box>
        {(user?.is_organization_admin || user?.is_superuser) && !mine && <Button component={Link} to="/settings/commissions" variant="outlined">Commission structure settings</Button>}
        <Button
          loading={exportRows.isPending}
          variant="outlined"
          onClick={() => exportRows.mutate()}
        >
          Export filtered statement
        </Button>
      </Stack>
      {!manage && (
        <Alert color="neutral" sx={{ mb: 2 }}>
          Your earnings are available here. Payouts and adjustments require
          commission management permission.
        </Alert>
      )}
      <SavedBillingViews kind={mine ? "my-commissions" : "commissions"} />
      <Card sx={{ mb: 2 }}>
        <Stack
          direction="row"
          sx={{ gap: 1, flexWrap: "wrap", alignItems: "end" }}
        >
          <FormControl>
            <FormLabel>Search</FormLabel>
            <Input
              placeholder="Job, address or rep…"
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Status</FormLabel>
            <Select
              value={filters.status || ""}
              onChange={(_, v) => setFilter("status", v || "")}
            >
              <Option value="">All</Option>
              {["accrued", "on_hold", "paid", "cancelled"].map((s) => (
                <Option key={s} value={s}>
                  {s.replace("_", " ")}
                </Option>
              ))}
            </Select>
          </FormControl>
          {!mine && manage && (
            <FormControl>
              <FormLabel>Sales rep</FormLabel>
              <Select
                value={String(filters.sales_rep || "")}
                onChange={(_, v) => setFilter("sales_rep", v || "")}
              >
                <Option value="">All reps</Option>
                {users.map((rep) => (
                  <Option key={rep.id} value={String(rep.id)}>
                    {[rep.first_name, rep.last_name]
                      .filter(Boolean)
                      .join(" ") || rep.email}
                  </Option>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl>
            <FormLabel>Date basis</FormLabel>
            <Select
              value={filters.date_field}
              onChange={(_, v) => setFilter("date_field", v || "accrued_at")}
            >
              <Option value="accrued_at">Accrued date</Option>
              <Option value="paid_at">Paid date</Option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>From</FormLabel>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilter("date_from", e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>To</FormLabel>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilter("date_to", e.target.value)}
            />
          </FormControl>
        </Stack>
      </Card>
      {summary.data && (
        <Card sx={{ mb: 2 }}>
          <Typography level="title-sm">
            Totals for the current filters
          </Typography>
          <Stack direction="row" sx={{ gap: 3, flexWrap: "wrap" }}>
            <Typography>Accrued {money(summary.data.total_accrued)}</Typography>
            <Typography>On hold {money(summary.data.total_on_hold)}</Typography>
            <Typography>
              Paid this month {money(summary.data.total_paid_this_month)}
            </Typography>
            <Typography>Total paid {money(summary.data.total_paid)}</Typography>
          </Stack>
        </Card>
      )}
      {(list.isError ||
        summary.isError ||
        change.isError ||
        exportRows.isError) && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {errorText(
            list.error || summary.error || change.error || exportRows.error,
          )}
          <Button
            onClick={() => {
              list.refetch();
              summary.refetch();
            }}
          >
            Refresh
          </Button>
        </Alert>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(String(v))} sx={{ mb: 2 }}>
        <TabList>
          <Tab value="commissions">Commissions</Tab>
          <Tab value="payouts">Payout history</Tab>
        </TabList>
      </Tabs>
      {tab === "payouts" ? (
        <PayoutHistory
          key={`${filters.sales_rep}:${filters.date_from}:${filters.date_to}`}
          salesRep={filters.sales_rep}
          dateFrom={filters.date_from}
          dateTo={filters.date_to}
        />
      ) : (
        <>
          {list.isLoading ? (
            <Typography>Loading commissions…</Typography>
          ) : (
            <ResponsiveTable<SalesCommission>
              rows={rows}
              getRowKey={(row) => row.id}
              pagination={{
                page,
                pageSize: 20,
                totalCount: list.data?.count || 0,
                onPageChange: (p) => setFilter("page", String(p)),
                itemNoun: "commissions",
              }}
              columns={[
                ...(manage
                  ? [
                      {
                        key: "select",
                        header: (
                          <Checkbox
                            slotProps={{
                              input: {
                                "aria-label":
                                  "Select payable commissions on this page",
                              },
                            }}
                            checked={
                              !!rows.filter((r) => r.status === "accrued")
                                .length &&
                              selected.length ===
                                rows.filter((r) => r.status === "accrued")
                                  .length
                            }
                            onChange={(e) =>
                              setSelection({
                                scope,
                                ids: e.target.checked
                                  ? rows
                                      .filter((r) => r.status === "accrued")
                                      .map((r) => r.id)
                                  : [],
                              })
                            }
                          />
                        ),
                        mobileLabel: "Select",
                        render: (row: SalesCommission) =>
                          row.status === "accrued" ? (
                            <Checkbox
                              slotProps={{
                                input: {
                                  "aria-label": `Select commission for job ${row.job_number}`,
                                },
                              }}
                              checked={selected.some((s) => s.id === row.id)}
                              onChange={() => toggle(row.id)}
                            />
                          ) : null,
                      },
                    ]
                  : []),
                {
                  key: "job",
                  header: "Job",
                  mobileVariant: "header",
                  render: (row) => (
                    <Button
                      variant="plain"
                      component={Link}
                      to={`/operations/jobs/${row.job}`}
                    >
                      #{row.job_number}
                    </Button>
                  ),
                },
                {
                  key: "rep",
                  header: "Sales rep",
                  render: (row) => (
                    <Typography>
                      {row.sales_rep_name}
                      <Typography level="body-xs">{row.job_address}</Typography>
                    </Typography>
                  ),
                },
                {
                  key: "structure",
                  header: "Calculation",
                  render: (row) => <Typography level="body-sm">{row.calculation_snapshot?.name || 'Original formula'}<Typography level="body-xs">{row.calculation_snapshot?.source === 'job' ? 'Job override' : 'Organization default'}</Typography></Typography>,
                },
                {
                  key: "amount",
                  header: "Amount",
                  render: (row) => (
                    <Typography fontWeight="lg">
                      {money(row.final_amount)}
                      {row.override_amount != null && (
                        <Typography level="body-xs">Adjusted</Typography>
                      )}
                    </Typography>
                  ),
                },
                {
                  key: "status",
                  header: "Status",
                  render: (row) => (
                    <Chip
                      size="sm"
                      color={
                        row.status === "paid"
                          ? "success"
                          : row.status === "on_hold"
                            ? "warning"
                            : "neutral"
                      }
                    >
                      {row.status.replace("_", " ")}
                    </Chip>
                  ),
                },
                {
                  key: "date",
                  header: "Accrued / paid",
                  render: (row) => (
                    <Typography level="body-sm">
                      {date(row.accrued_at)}
                      {row.paid_at && ` / ${date(row.paid_at)}`}
                    </Typography>
                  ),
                },
              ]}
              rowActions={(row) => (
                <Stack direction="row" sx={{ gap: 0.5, flexWrap: "wrap" }}>
                  <Button
                    size="sm"
                    variant="plain"
                    onClick={() => setDetail(row.id)}
                  >
                    Details
                  </Button>
                  {manage && row.status === "accrued" && (
                    <Button
                      size="sm"
                      variant="plain"
                      color="warning"
                      onClick={() => openAction(row, "hold")}
                    >
                      Hold
                    </Button>
                  )}
                  {manage && row.status === "on_hold" && (
                    <Button
                      size="sm"
                      loading={change.isPending}
                      onClick={() =>
                        change.mutate({ id: row.id, kind: "release", data: {} })
                      }
                    >
                      Release
                    </Button>
                  )}
                  {manage && ["accrued", "on_hold"].includes(row.status) && (
                    <Button
                      size="sm"
                      variant="plain"
                      onClick={() => openAction(row, "override")}
                    >
                      Adjust
                    </Button>
                  )}
                </Stack>
              )}
            />
          )}
          {selected.length > 0 && manage && (
            <Card
              color="primary"
              variant="soft"
              sx={{ position: "sticky", bottom: 12, mt: 2, zIndex: 2 }}
            >
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography>
                  {selected.length} selected · {money(total)} · {groups.length}{" "}
                  rep groups
                </Typography>
                <Button
                  onClick={() => {
                    setRequestId(crypto.randomUUID());
                    payment.reset();
                    setPayOpen(true);
                  }}
                >
                  Review recorded payment
                </Button>
              </Stack>
            </Card>
          )}
        </>
      )}
      <Modal
        open={payOpen}
        onClose={() => !payment.isPending && setPayOpen(false)}
      >
        <ModalDialog
          sx={{
            width: "min(640px, calc(100vw - 24px))",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <ModalClose />
          <Typography level="h4">Review commission payments</Typography>
          <Alert color="neutral">
            This records payments you have made. It does not initiate a bank
            transfer.
          </Alert>
          {groups.map((group) => (
            <Card key={group.rep}>
              <Typography fontWeight="lg">
                {group.rep} · {money(group.total)}
              </Typography>
              {group.rows.map((row) => (
                <Typography key={row.id} level="body-sm">
                  Job #{row.job_number} · {money(row.final_amount)}
                </Typography>
              ))}
            </Card>
          ))}
          <Typography fontWeight="lg">
            Total to record: {money(total)}
          </Typography>
          <FormControl>
            <FormLabel>Payment method</FormLabel>
            <Select value={method} onChange={(_, v) => setMethod(v || "check")}>
              {["check", "direct_deposit", "cash", "other"].map((v) => (
                <Option key={v} value={v}>
                  {v.replace("_", " ")}
                </Option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Payment or batch reference (optional)</FormLabel>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Notes (optional)</FormLabel>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </FormControl>
          {payment.isError && (
            <Alert color="danger">{errorText(payment.error)}</Alert>
          )}
          <Button
            disabled={!selected.length}
            loading={payment.isPending}
            onClick={() => payment.mutate()}
          >
            Confirm recorded payments
          </Button>
        </ModalDialog>
      </Modal>
      <Modal
        open={!!action}
        onClose={() => !change.isPending && setAction(null)}
      >
        <ModalDialog
          sx={{
            width: "min(520px, calc(100vw - 24px))",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <ModalClose />
          <Typography level="h4">
            {action?.kind === "hold" ? "Hold commission" : "Adjust commission"}
          </Typography>
          <Typography>
            {action?.row.sales_rep_name} · Job #{action?.row.job_number} ·
            Current {money(action?.row.final_amount)}
          </Typography>
          <FormControl>
            <FormLabel>Reason</FormLabel>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </FormControl>
          {action?.kind === "hold" ? (
            <>
              <FormControl>
                <FormLabel>Follow-up owner</FormLabel>
                <Select value={owner} onChange={(_, v) => setOwner(v)}>
                  {users.map((u) => (
                    <Option key={u.id} value={u.id}>
                      {[u.first_name, u.last_name].filter(Boolean).join(" ") ||
                        u.email}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Follow-up date (optional)</FormLabel>
                <Input
                  type="date"
                  value={followUp}
                  onChange={(e) => setFollowUp(e.target.value)}
                />
              </FormControl>
            </>
          ) : (
            <FormControl>
              <FormLabel>New amount</FormLabel>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                slotProps={{ input: { min: 0, step: 0.01 } }}
              />
            </FormControl>
          )}
          {change.isError && (
            <Alert color="danger">{errorText(change.error)}</Alert>
          )}
          <Button
            disabled={!reason.trim() || !action}
            loading={change.isPending}
            onClick={() =>
              action &&
              change.mutate({
                id: action.row.id,
                kind: action.kind,
                data:
                  action.kind === "hold"
                    ? { reason, owner, follow_up_date: followUp || null }
                    : { reason, amount },
              })
            }
          >
            Save {action?.kind === "hold" ? "hold" : "adjustment"}
          </Button>
        </ModalDialog>
      </Modal>
      {detail && (
        <CommissionDetail id={detail} onClose={() => setDetail(null)} />
      )}
    </Box>
  );
}
export function CommissionDetail({
  id,
  onClose,
}: {
  id: number;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ["commission-detail", user?.organization, user?.id, id],
    queryFn: async () =>
      (await apiClient.get<SalesCommission>(`/api/sales-commissions/${id}/`))
        .data,
  });
  const activity = useQuery({
    queryKey: ["commission-activity", user?.organization, user?.id, id, page],
    queryFn: async () =>
      (
        await apiClient.get<any>(
          `/api/sales-commissions/${id}/activity/?page=${page}`,
        )
      ).data,
  });
  const c = query.data;
  return (
    <Modal open onClose={onClose}>
      <ModalDialog
        sx={{
          width: "min(720px, calc(100vw - 24px))",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <ModalClose />
        <Typography level="h4">Commission details</Typography>
        {query.isError && (
          <Alert color="danger">{errorText(query.error)}</Alert>
        )}
        {c && (
          <>
            <Typography>
              {c.sales_rep_name} · Job #{c.job_number}
            </Typography>
            <Button
              component={Link}
              to={`/operations/jobs/${c.job}?tab=financials`}
              variant="plain"
            >
              Open job
            </Button>
            <Card>
              {c.calculation_snapshot?.rules ? <>
                <Typography level="title-md">{c.calculation_snapshot.name}</Typography>
                <Chip size="sm">{c.calculation_snapshot.source === 'job' ? 'Job override at accrual' : 'Organization structure at accrual'}</Chip>
                <CommissionBreakdown calculation={c.calculation_snapshot} />
              </> : <>
                <Typography>Revenue {money(c.job_value)} − costs {money(c.total_costs)} = profit {money(c.profit)}</Typography>
                <Typography>Overhead {(Number(c.overhead_rate) * 100).toFixed(2)}% · Commissionable {money(c.commissionable_amount)}</Typography>
                <Typography>Rate {c.commission_rate}% · Calculated {money(c.commission_amount)}</Typography>
              </>}
              <Typography fontWeight="lg">
                Final commission {money(c.final_amount)}
              </Typography>
              {c.override_amount != null && (
                <Typography>Adjustment reason: {c.override_reason}</Typography>
              )}
              {c.hold_reason && <Alert color="warning">{c.hold_reason}</Alert>}
            </Card>
            <Typography level="body-sm">
              Amounts are snapshots at accrual time. Paid history is preserved.
            </Typography>
          </>
        )}
        <Divider />
        <Typography level="title-lg">Activity</Typography>
        {activity.isError && (
          <Alert color="danger">{errorText(activity.error)}</Alert>
        )}
        {activity.data?.results.map((a: any) => (
          <Card key={a.id}>
            <Typography fontWeight="lg">{a.title}</Typography>
            <Typography level="body-sm">
              {a.actor} · {new Date(a.at).toLocaleString()}
            </Typography>
            {Object.entries(a.details.after || {}).map(([k, v]) => (
              <Typography key={k} level="body-sm">
                {k.replaceAll("_", " ")}: {String(a.details.before?.[k] ?? "—")}{" "}
                → {String(v)}
              </Typography>
            ))}
            {a.details.owner && (
              <Typography>
                Follow up: {a.details.owner} ·{" "}
                {a.details.follow_up_date || "No date set"}
              </Typography>
            )}
          </Card>
        ))}
        {!activity.isLoading && !activity.data?.count && (
          <Typography>No recorded changes yet.</Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Newer
          </Button>
          <Button
            disabled={!activity.data?.next}
            onClick={() => setPage((p) => p + 1)}
          >
            Older
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
function PayoutHistory({
  salesRep,
  dateFrom,
  dateTo,
}: {
  salesRep?: number | "";
  dateFrom?: string;
  dateTo?: string;
}) {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const filters = {
    page,
    ...(salesRep ? { sales_rep: salesRep } : {}),
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
  };
  const query = useQuery({
    queryKey: ["commissionPayouts", user?.organization, user?.id, filters],
    queryFn: () => fetchPayouts(filters),
  });
  return (
    <Stack spacing={2}>
      <Typography level="body-sm">
        History uses payout completion dates for the selected date range. Each
        payout represents a recorded payment.
      </Typography>
      {query.isError && (
        <Alert color="danger">
          {errorText(query.error)}
          <Button
            onClick={() => {
              setPage(1);
              query.refetch();
            }}
          >
            Retry
          </Button>
        </Alert>
      )}
      {query.isLoading && <Typography>Loading payouts…</Typography>}
      {query.data?.results.map((p: any) => (
        <Card key={p.id}>
          <Typography level="title-md">
            {p.payout_number} · {p.sales_rep_name} · {money(p.total_amount)}
          </Typography>
          <Typography>
            {date(p.completed_at)} · {p.payment_method.replace("_", " ")} ·{" "}
            {p.payment_reference}
          </Typography>
          <Typography level="body-sm">
            Accounting:{" "}
            {p.accounting_posted
              ? "Posted"
              : "Not posted — review account configuration"}
          </Typography>
          <details>
            <summary>{p.commission_count} commissions</summary>
            {p.commissions.map((c: SalesCommission) => (
              <Typography key={c.id}>
                Job #{c.job_number} · {money(c.final_amount)}
              </Typography>
            ))}
          </details>
          <Button
            variant="outlined"
            size="sm"
            onClick={() =>
              download(
                [
                  [
                    "Payout",
                    "Sales rep",
                    "Job",
                    "Amount",
                    "Completed",
                    "Reference",
                  ],
                  ...p.commissions.map((c: SalesCommission) => [
                    p.payout_number,
                    p.sales_rep_name,
                    c.job_number,
                    c.final_amount,
                    p.completed_at,
                    p.payment_reference,
                  ]),
                ],
                `${p.payout_number}.csv`,
              )
            }
          >
            Download payout statement
          </Button>
        </Card>
      ))}
      {!query.isLoading && !query.data?.count && (
        <Typography>No payouts match this range.</Typography>
      )}
      <Stack direction="row" spacing={1}>
        <Button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          Newer
        </Button>
        <Typography>Page {page}</Typography>
        <Button
          disabled={!query.data?.next}
          onClick={() => setPage((p) => p + 1)}
        >
          Older
        </Button>
      </Stack>
    </Stack>
  );
}
